import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useMemo, type ReactNode } from "react";

import { queryKeys } from "../query";
import { resolveTenantSlug } from "./resolveTenant";

export interface TenantBranding {
  readonly displayName: string;
  readonly logoUrl?: string;
  readonly primaryColor?: string;
}

/** The caller's Origin, confirmed against a real, live tenant. */
export interface TenantIdentity {
  readonly slug: string;
  readonly nameEn: string;
  readonly nameAr: string;
}

export interface TenantContextValue {
  readonly slug: string | null;
  readonly branding: TenantBranding | null;
  readonly brandingLoading: boolean;
  readonly identity: TenantIdentity | null;
  /**
   * The tenant couldn't be confirmed for any reason -- a documented 404, a
   * server error, a CORS rejection, anything other than a clean 200.
   */
  readonly tenantNotFound: boolean;
}

export interface TenantProviderProps {
  readonly children: ReactNode;
  readonly hostname?: string;
  readonly devOverride?: string;
  /** No slug argument: the backend resolves the tenant from Origin, same as every other request. */
  readonly loadBranding?: () => Promise<TenantBranding>;
  /** Confirms the Origin names a real tenant; rejects (404) when it doesn't. */
  readonly resolveTenant?: () => Promise<TenantIdentity>;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({
  children,
  hostname = typeof window === "undefined" ? "" : window.location.hostname,
  devOverride,
  loadBranding,
  resolveTenant,
}: TenantProviderProps) {
  const slug = resolveTenantSlug({
    hostname,
    ...(devOverride === undefined ? {} : { devOverride }),
  });
  const brandingQuery = useQuery({
    queryKey: queryKeys.tenantBranding.detail(slug ?? "none"),
    queryFn: () => {
      if (slug === null || loadBranding === undefined) {
        throw new Error("Tenant branding loader is not configured.");
      }
      return loadBranding();
    },
    enabled: slug !== null && loadBranding !== undefined,
    staleTime: 5 * 60_000,
    // A tenant with no branding configured is a normal outcome, not a transient failure.
    retry: false,
  });
  const resolveQuery = useQuery({
    queryKey: queryKeys.tenantResolve.detail(slug ?? "none"),
    queryFn: () => {
      if (slug === null || resolveTenant === undefined) {
        throw new Error("Tenant resolver is not configured.");
      }
      return resolveTenant();
    },
    enabled: slug !== null && resolveTenant !== undefined,
    staleTime: 5 * 60_000,
    // Any failure here -- a documented 404, a 5xx, a CORS rejection, a
    // dropped connection -- means this Origin can't be confirmed as a real
    // tenant, so none of it is worth retrying: they're all the same "block
    // the app" outcome to the caller.
    retry: false,
  });
  const tenantNotFound = resolveQuery.isError;
  const value = useMemo<TenantContextValue>(
    () => ({
      slug,
      branding: brandingQuery.data ?? null,
      brandingLoading: brandingQuery.isLoading,
      identity: resolveQuery.data ?? null,
      tenantNotFound,
    }),
    [
      brandingQuery.data,
      brandingQuery.isLoading,
      resolveQuery.data,
      slug,
      tenantNotFound,
    ],
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const value = useContext(TenantContext);
  if (value === undefined) {
    throw new Error("useTenant must be used within TenantProvider.");
  }
  return value;
}
